import { useEffect, useRef, useState, useCallback } from "react";
import { Video, Plus, Users, Loader2, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import VideoRoom from "@/components/VideoRoom";

export default function Meetings() {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [active, setActive] = useState(null); // { id, channel, name }
  const hbRef = useRef(null);

  const loadRooms = useCallback(async () => {
    try {
      const r = await api.get("/rooms");
      setRooms(r.data);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    if (active) return; // pause lobby polling while in a call
    loadRooms();
    const int = setInterval(loadRooms, 4000);
    return () => clearInterval(int);
  }, [active, loadRooms]);

  const createRoom = async (e) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    setCreating(true);
    try {
      const r = await api.post("/rooms", { name: n });
      setName("");
      await loadRooms();
      join(r.data);
    } catch (e) {
      toast.error("Could not create the room");
    } finally {
      setCreating(false);
    }
  };

  const join = async (room) => {
    try {
      const r = await api.post(`/rooms/${room.id}/join`);
      setActive({ id: room.id, channel: r.data.channel, name: room.name });
      hbRef.current = setInterval(() => {
        api.post(`/rooms/${room.id}/heartbeat`).catch(() => {});
      }, 10000);
    } catch (e) {
      toast.error("Could not join the room");
    }
  };

  const leave = async () => {
    if (hbRef.current) clearInterval(hbRef.current);
    hbRef.current = null;
    if (active) api.post(`/rooms/${active.id}/leave`).catch(() => {});
    setActive(null);
    loadRooms();
  };

  const removeRoom = async (room, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/rooms/${room.id}`);
      setRooms((rs) => rs.filter((r) => r.id !== room.id));
    } catch (err) {
      toast.error("Only the room creator can delete it");
    }
  };

  useEffect(() => () => { if (hbRef.current) clearInterval(hbRef.current); }, []);

  if (active) {
    return (
      <div className="flex-1 min-w-0">
        <VideoRoom channel={active.channel} title={active.name} onLeave={leave} />
      </div>
    );
  }

  return (
    <div className="flex-1 min-w-0 bg-[#16181F] overflow-y-auto" data-testid="meetings-lobby">
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center gap-3 mb-1">
          <span className="w-10 h-10 rounded-xl ruby-gradient flex items-center justify-center ember-glow">
            <Video className="w-5 h-5 text-white" />
          </span>
          <div>
            <h1 className="font-display font-bold text-xl text-gray-100">Meetings</h1>
            <p className="text-sm text-gray-500">Start or join a team video call — camera, mic & screen share.</p>
          </div>
        </div>

        <form onSubmit={createRoom} className="mt-6 flex gap-2">
          <Input
            data-testid="new-room-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Room name — e.g. Daily standup"
            className="bg-[#0F1015] border-[#2E3242] focus-visible:ring-red-600 h-11"
          />
          <Button
            data-testid="create-room-button"
            type="submit"
            disabled={!name.trim() || creating}
            className="ruby-gradient text-white border border-red-900/40 h-11 px-4"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1.5" />}
            New room
          </Button>
        </form>

        <div className="mt-8 space-y-3">
          <p className="text-[11px] uppercase tracking-wider text-gray-500">Active rooms</p>
          {rooms.length === 0 && (
            <div className="rounded-xl border border-dashed border-[#2E3242] py-10 text-center text-gray-600">
              No rooms yet. Create one to start a call.
            </div>
          )}
          {rooms.map((room) => (
            <div
              key={room.id}
              data-testid={`room-${room.id}`}
              className="flex items-center justify-between gap-2 sm:gap-3 rounded-xl border border-[#2E3242] bg-[#1E2029] px-3 sm:px-4 py-3 hover:border-red-900/40 transition-colors"
            >
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm text-gray-100 truncate">{room.name}</p>
                <p className="text-[11px] text-gray-500 truncate">by {room.created_by_name || "someone"}</p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                {room.participant_count > 0 && (
                  <div className="hidden sm:flex items-center -space-x-2">
                    {room.participants.slice(0, 4).map((p) => (
                      <img key={p.user_id} src={p.avatar} alt={p.name}
                        className="w-7 h-7 rounded-full object-cover border-2 border-[#1E2029]" />
                    ))}
                  </div>
                )}
                <span className="text-[11px] text-gray-500 flex items-center gap-1">
                  <Users className="w-3.5 h-3.5" /> {room.participant_count}
                </span>
                <Button
                  data-testid={`join-room-${room.id}`}
                  onClick={() => join(room)}
                  className="ruby-gradient text-white border border-red-900/40 h-9 px-3"
                >
                  <Video className="w-4 h-4 sm:mr-1.5" /> <span className="hidden sm:inline">Join</span>
                </Button>
                {(room.created_by === user?.id || user?.role === "admin") && (
                  <button
                    data-testid={`delete-room-${room.id}`}
                    onClick={(e) => removeRoom(room, e)}
                    className="w-9 h-9 rounded-md flex items-center justify-center text-gray-500 hover:text-red-400 border border-[#2E3242] hover:border-red-900/40 transition-colors shrink-0"
                    title="Delete room"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
